import type { MouseEventHandler, PointerEventHandler } from 'react';
import { useCallback, useState } from 'react';

/**
 * If the pointer moves more than this many pixels, the click will be ignored.
 */
const DISTANCE_THRESHOLD = 10;

/**
 * Check if a click event was fired after a mouse movement.
 *
 * Spread the return value from this hook as attributes onto the element
 * you want to handle clicks on.
 */
export function useClickIgnoreDrag<T = Element>(onClick: MouseEventHandler<T>) {
	const [dragStartCoordinates, setDragStartCoordinates] =
		useState<[number, number]>();
	const [dragStartTime, setDragStartTime] = useState<number>();

	const onPointerDown: PointerEventHandler = useCallback((e) => {
		setDragStartCoordinates([e.clientX, e.clientY]);
		setDragStartTime(performance.now());
	}, []);

	const onPointerUp: PointerEventHandler<T> = useCallback(
		(e) => {
			if (!dragStartCoordinates) {
				return;
			}

			// Ignore if the pointer has been held down for 750 ms
			if (dragStartTime && performance.now() - dragStartTime >= 750) {
				return;
			}

			const [startX, startY] = dragStartCoordinates;
			const [endX, endY] = [e.clientX, e.clientY];

			if (
				Math.abs(startX - endX) <= DISTANCE_THRESHOLD &&
				Math.abs(startY - endY) <= DISTANCE_THRESHOLD
			) {
				onClick(e);
			}
		},
		[dragStartCoordinates, dragStartTime, onClick]
	);

	/**
	 * Defined for compatibility if browsers don't have support for pointer events
	 * (e.g. Safari < 13).
	 *
	 * @link https://caniuse.com/pointer
	 */
	const onClickResult: MouseEventHandler<T> = useCallback(
		(e) => {
			if (!dragStartCoordinates) {
				onClick(e);
			}
		},
		[dragStartCoordinates, onClick]
	);

	return {
		onPointerDown,
		onPointerUp,
		onClick: onClickResult,
	};
}
